/**
 * SetupWizardPage
 * 
 * P0: Single setup wizard for new users (first-login success)
 * - Completes user setup (state, attorney info)
 * - Creates first case
 * - Creates first journal entry
 * - Resumable (persists draft to Firestore)
 * - Skippable (with empty state CTA)
 * 
 * WO-SETUP-WIZARD-001
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, ChevronLeft, ArrowRight, Upload, X } from 'lucide-react';
import { doc, updateDoc, setDoc, collection, Timestamp, getDoc } from 'firebase/firestore';
import { db } from '@/firebase';
import { useAuth } from '@/auth/AuthProvider';
import { AuthWizardLayout } from '@/layout/AuthWizardLayout';
import { toast } from 'react-hot-toast';

// US States constant (51 items: 50 states + DC)
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
  'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
  'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
  'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
  'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'Washington D.C.', 'West Virginia', 'Wisconsin', 'Wyoming'
];

// State code mapping for jurisdiction keys
const STATE_CODE_MAP: Record<string, string> = {
  'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR',
  'California': 'CA', 'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE',
  'Florida': 'FL', 'Georgia': 'GA', 'Hawaii': 'HI', 'Idaho': 'ID',
  'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA', 'Kansas': 'KS',
  'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
  'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS',
  'Missouri': 'MO', 'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV',
  'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
  'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH', 'Oklahoma': 'OK',
  'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT',
  'Vermont': 'VT', 'Virginia': 'VA', 'Washington': 'WA', 'Washington D.C.': 'DC',
  'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY'
};

// Setup draft state type
type SetupDraft = {
  filedState: string | null;
  
  hasAttorney: boolean | null;
  attorney?: {
    name: string;
    email: string;
    firm?: string;
    phone?: string;
  };
  
  hasFiledCase: boolean | null;
  case?: {
    title: string;
    court?: string;
    county?: string;
    caseNumber?: string;
  };
  
  wantsToUploadFilesNow: boolean | null;
  initialUploads?: File[]; // UI-only; storage handled on submit
  
  initialJournalEntry: string; // required, default provided
};

// Default journal entry text
const getDefaultJournalText = () => {
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  return `${today}. I created my ThreadLock account to start organizing my case.`;
};

// Wizard steps
const STEPS = {
  STATE: 0,
  ATTORNEY: 1,
  CASE: 2,
  JOURNAL: 3,
  REVIEW: 4
};

export default function SetupWizardPage() {
  const { user, claims } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');

  const stepFromUrl = parseInt(searchParams.get('step') ?? '0', 10);
  const [currentStep, setCurrentStep] = useState(isNaN(stepFromUrl) ? STEPS.STATE : stepFromUrl);
  const [isExiting, setIsExiting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [formData, setFormData] = useState<SetupDraft>({
    filedState: null,
    hasAttorney: null,
    hasFiledCase: null,
    wantsToUploadFilesNow: null,
    initialJournalEntry: getDefaultJournalText()
  });

  // Load existing draft on mount
  useEffect(() => {
    if (!user) return;

    const loadDraft = async () => {
      try {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data();

        // Pro users never go through SRL onboarding
        const claimsHasPro =
          claims?.isPro === true ||
          (claims as Record<string, unknown>)?.hasPro === true ||
          (claims as Record<string, unknown>)?.pro === true;
        if (claimsHasPro) {
          console.log('[SetupWizard] Pro user detected - redirecting to /pro/dashboard');
          navigate('/pro/dashboard', { replace: true });
          return;
        }

        // If setup is already completed, redirect to dashboard
        if (userData?.onboarding?.setupCompleted) {
          console.log('[SetupWizard] Setup already completed, redirecting...');
          navigate(returnTo || '/dashboard', { replace: true });
          return;
        }

        if (userData?.onboarding?.setupDraft) {
          const draft = userData.onboarding.setupDraft;
          setFormData({
            ...draft,
            initialJournalEntry: draft.initialJournalEntry || getDefaultJournalText()
          });
          
          // Resume from last step if available (prefer URL param if already set)
          if (userData.onboarding.setupLastStep !== undefined && !searchParams.get('step')) {
            const resumeStep = userData.onboarding.setupLastStep;
            setCurrentStep(resumeStep);
            setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('step', String(resumeStep)); return p; }, { replace: true });
          }
        }
      } catch (error) {
        console.error('[SetupWizard] Failed to load draft:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadDraft();
  }, [user, navigate, returnTo]);

  // Save draft to Firestore
  const saveDraft = async (stepNum: number) => {
    if (!user) return;

    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        'onboarding.setupDraft': formData,
        'onboarding.setupLastStep': stepNum,
        'onboarding.setupUpdatedAt': Timestamp.now()
      });
    } catch (error) {
      console.error('[SetupWizard] Failed to save draft:', error);
      toast.error('Failed to save progress');
    }
  };

  // Skip wizard
  const handleSkip = async () => {
    if (!user) return;

    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        'onboarding.setupSkippedAt': Timestamp.now(),
        'onboarding.setupCompleted': true  // Mark as completed so wizard doesn't show again
      });
      
      toast.success('Setup wizard skipped');
      navigate(returnTo || '/dashboard', { replace: true });
    } catch (error) {
      console.error('[SetupWizard] Failed to mark skip:', error);
      toast.error('Failed to skip setup');
      // Still navigate even if update failed
      navigate(returnTo || '/dashboard', { replace: true });
    }
  };

  // Navigate between steps
  const handleNext = async () => {
    if (!canProceed()) return;

    // Save draft before moving forward
    await saveDraft(currentStep + 1);

    // ONBOARDING DATA CONTRACT: Persist roleConfirmed after STATE step
    // This guarantees durable state exists for wizard.setup.next affordance
    if (currentStep === STEPS.STATE && user) {
      try {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          'onboarding.roleConfirmed': true,
          'onboarding.roleConfirmedAt': Timestamp.now()
        });
      } catch (error) {
        console.error('[SetupWizard] Failed to persist roleConfirmed:', error);
        // Don't block progression
      }
    }

    setIsExiting(true);
    setTimeout(() => {
      let nextStep = currentStep + 1;

      // Skip attorney details if no attorney
      if (currentStep === STEPS.STATE && formData.hasAttorney === false) {
        nextStep = STEPS.CASE;
      }

      setCurrentStep(nextStep);
      setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('step', String(nextStep)); return p; }, { replace: true });
      setIsExiting(false);
    }, 300);
  };

  const handleBack = () => {
    setIsExiting(true);
    setTimeout(() => {
      let prevStep = currentStep - 1;

      // Skip back over attorney if it was skipped forward
      if (currentStep === STEPS.CASE && formData.hasAttorney === false) {
        prevStep = STEPS.STATE;
      }

      setCurrentStep(prevStep);
      setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('step', String(prevStep)); return p; }, { replace: true });
      setIsExiting(false);
    }, 300);
  };

  // Validation for each step
  const canProceed = () => {
    switch (currentStep) {
      case STEPS.STATE:
        return formData.filedState !== null;
      
      case STEPS.ATTORNEY:
        if (formData.hasAttorney === null) return false;
        if (formData.hasAttorney === false) return true;
        return (
          formData.attorney?.name &&
          formData.attorney?.name.length > 2 &&
          formData.attorney?.email &&
          /\S+@\S+\.\S+/.test(formData.attorney.email)
        );
      
      case STEPS.CASE:
        if (formData.hasFiledCase === null) return false;
        if (formData.hasFiledCase === false) return true;
        return formData.case?.title && formData.case.title.length > 2;
      
      case STEPS.JOURNAL:
        return formData.initialJournalEntry.trim().length > 10;
      
      case STEPS.REVIEW:
        return true;
      
      default:
        return false;
    }
  };

  // Complete setup
  const handleComplete = async () => {
    if (!user) return;

    setIsSaving(true);
    try {
      const userRef = doc(db, 'users', user.uid);

      // 1. Create case first (so we can store initialCaseId)
      const caseTitle = formData.hasFiledCase && formData.case?.title 
        ? formData.case.title 
        : 'My Case';

      const stateCode = STATE_CODE_MAP[formData.filedState!];
      const caseRef = doc(db, 'cases', `case-${user.uid}-${Date.now()}`);
      
      await setDoc(caseRef, {
        ownerUid: user.uid,
        partyId: user.uid,
        title: caseTitle,
        title_lc: caseTitle.toLowerCase(),
        description: '',
        state: formData.filedState,
        stateCode: stateCode,
        jurisdictionKey: `US-${stateCode}`,
        courtType: 'general',
        caseNumber: formData.case?.caseNumber || '',
        status: 'active',
        memberIds: [user.uid],
        roles: { [user.uid]: 'owner' },
        memberUids: [user.uid],
        counselIds: [],
        evaluatorIds: [],
        counters: { nextExhibitNumber: 1, nextBates: 1 },
        storageUsedBytes: 0, // TODO: WO-STORAGE-QUOTA-001 — increment on R2 upload, enforce per plan
        onboardingCompleted: true, // P4 FIX: User already completed setup wizard, skip case onboarding
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      // 2. Update user profile with ONBOARDING DATA CONTRACT fields
      const profileUpdate: any = {
        'profile.state': formData.filedState,
        'onboarding.setupCompleted': true,
        'onboarding.completedAt': Timestamp.now(),
        'onboarding.roleConfirmed': true,
        'onboarding.initialCaseId': caseRef.id
      };

      if (formData.hasAttorney && formData.attorney) {
        profileUpdate['profile.attorney'] = formData.attorney;
      }

      await updateDoc(userRef, profileUpdate);

      // 3. Create first journal entry
      const journalRef = doc(collection(db, 'cases', caseRef.id, 'journalEntries'));
      await setDoc(journalRef, {
        caseId: caseRef.id,
        userId: user.uid,
        date: Timestamp.now(),
        description: formData.initialJournalEntry.trim(),
        tags: [],
        attachments: [],
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      // P4: Wait for billing data to sync before navigating to dashboard
      // This prevents the RequireSubscription redirect dead end
      console.info('[SetupWizard] Verifying billing data before dashboard redirect...');
      try {
        const { doc: firestoreDoc, getDoc } = await import('firebase/firestore');
        const billingRef = firestoreDoc(db, 'billingCustomers', user.uid);
        
        let attempts = 0;
        const maxAttempts = 10; // 10 seconds max
        let billingExists = false;
        
        while (attempts < maxAttempts && !billingExists) {
          const billingSnap = await getDoc(billingRef);
          if (billingSnap.exists() && billingSnap.data()?.subscriptionStatus) {
            billingExists = true;
            console.info('[SetupWizard] ✅ Billing data verified:', {
              status: billingSnap.data()?.subscriptionStatus,
              type: billingSnap.data()?.subscriptionType
            });
          } else {
            attempts++;
            if (attempts < maxAttempts) {
              console.info(`[SetupWizard] Billing not ready, attempt ${attempts}/${maxAttempts}...`);
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          }
        }
        
        if (!billingExists) {
          console.warn('[SetupWizard] ⚠️ Billing data not ready after 10s - proceeding anyway');
        }
      } catch (verifyError) {
        console.error('[SetupWizard] Failed to verify billing:', verifyError);
        // Continue anyway
      }

      // Navigate to dashboard
      toast.success('Setup complete! Welcome to ThreadLock.');
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('[SetupWizard] Failed to complete setup:', error);
      toast.error('Failed to complete setup. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Calculate progress
  const totalSteps = 5;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  if (isLoading) {
    return (
      <AuthWizardLayout>
        <div data-testid="wizard.loading" className="text-center text-slate-300">Loading...</div>
      </AuthWizardLayout>
    );
  }

  return (
    <AuthWizardLayout>
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-center gap-2 mb-4">
          {Array.from({ length: totalSteps }).map((_, index) => (
            <div
              key={index}
              className={`h-2 rounded-full transition-all ${
                index === currentStep
                  ? 'bg-brand-orange w-16'
                  : index < currentStep
                  ? 'bg-brand-orange w-12'
                  : 'bg-slate-700 w-12'
              }`}
            />
          ))}
        </div>
        <div className="text-center text-sm text-slate-400">
          Step {currentStep + 1} of {totalSteps}
        </div>
      </div>

      {/* Step Content */}
      <div
        className={`transition-all duration-300 ${
          isExiting ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
        }`}
      >
        {currentStep === STEPS.STATE && (
          <StateStep formData={formData} setFormData={setFormData} />
        )}
        
        {currentStep === STEPS.ATTORNEY && (
          <AttorneyStep formData={formData} setFormData={setFormData} />
        )}
        
        {currentStep === STEPS.CASE && (
          <CaseStep formData={formData} setFormData={setFormData} />
        )}
        
        {currentStep === STEPS.JOURNAL && (
          <JournalStep formData={formData} setFormData={setFormData} />
        )}
        
        {currentStep === STEPS.REVIEW && (
          <ReviewStep formData={formData} />
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8">
        {currentStep > 0 ? (
          <button
            data-testid="wizard.back"
            onClick={handleBack}
            className="flex items-center gap-2 text-slate-400 font-medium px-4 py-2 rounded-lg hover:bg-slate-700 transition-all"
          >
            <ChevronLeft size={20} /> Back
          </button>
        ) : (
          <div></div>
        )}

        <div className="flex gap-3">
          {currentStep < STEPS.REVIEW && (
            <button
              data-testid="wizard.skip"
              onClick={handleSkip}
              className="text-slate-400 hover:text-slate-300 px-4 py-2 text-sm"
            >
              Skip for now
            </button>
          )}

          {currentStep < STEPS.REVIEW ? (
            <button
              data-testid="wizard.setup.next"
              onClick={handleNext}
              disabled={!canProceed()}
              className={`flex items-center gap-2 bg-brand-orange text-white px-6 py-3 rounded-lg font-semibold transition-all ${
                !canProceed()
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-brand-orange/90 hover:shadow-lg hover:shadow-brand-orange/20 hover:-translate-y-0.5'
              }`}
            >
              Continue <ArrowRight size={20} />
            </button>
          ) : (
            <button
              data-testid="wizard.finish"
              onClick={handleComplete}
              disabled={isSaving}
              className="flex items-center gap-2 bg-brand-orange text-white px-6 py-3 rounded-lg font-semibold hover:bg-brand-orange/90 hover:shadow-lg hover:shadow-brand-orange/20 hover:-translate-y-0.5 transition-all disabled:opacity-50"
            >
              {isSaving ? 'Setting up...' : 'Complete Setup'} <Check size={20} />
            </button>
          )}
        </div>
      </div>
    </AuthWizardLayout>
  );
}

// Step Components

function StateStep({ formData, setFormData }: { formData: SetupDraft; setFormData: (data: SetupDraft) => void }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Let's start with the basics</h2>
        <p className="text-slate-300">Which state is your case filed in?</p>
      </div>

      <div>
        <label htmlFor="state" className="block text-sm font-medium text-slate-300 mb-2">
          State where your case is filed
        </label>
        <select
          id="state"
          value={formData.filedState || ''}
          onChange={(e) => setFormData({ ...formData, filedState: e.target.value })}
          className="w-full px-4 py-3 rounded-lg border border-slate-700/50 bg-slate-800/50 text-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent"
        >
          <option value="">Select a state...</option>
          {US_STATES.map((state) => (
            <option key={state} value={state}>
              {state}
            </option>
          ))}
        </select>
      </div>

      {formData.filedState && (
        <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg text-sm text-blue-200">
          We'll organize everything specific to {formData.filedState}.
        </div>
      )}

      {/* Attorney toggle moved to next step for cleaner UX */}
      <div className="mt-8">
        <label className="block text-sm font-medium text-slate-300 mb-3">
          Do you have an attorney representing you?
        </label>
        <div className="flex gap-4">
          <button
            onClick={() => setFormData({ ...formData, hasAttorney: true })}
            className={`flex-1 px-6 py-4 rounded-lg border-2 transition-all ${
              formData.hasAttorney === true
                ? 'border-brand-orange bg-brand-orange/10 text-white'
                : 'border-slate-700/50 bg-slate-800/50 text-slate-300 hover:border-slate-600'
            }`}
          >
            Yes
          </button>
          <button
            onClick={() => setFormData({ ...formData, hasAttorney: false, attorney: undefined })}
            className={`flex-1 px-6 py-4 rounded-lg border-2 transition-all ${
              formData.hasAttorney === false
                ? 'border-brand-orange bg-brand-orange/10 text-white'
                : 'border-slate-700/50 bg-slate-800/50 text-slate-300 hover:border-slate-600'
            }`}
          >
            No
          </button>
        </div>
      </div>
    </div>
  );
}

function AttorneyStep({ formData, setFormData }: { formData: SetupDraft; setFormData: (data: SetupDraft) => void }) {
  const updateAttorney = (field: keyof NonNullable<SetupDraft['attorney']>, value: string) => {
    setFormData({
      ...formData,
      attorney: {
        ...formData.attorney,
        [field]: value
      } as NonNullable<SetupDraft['attorney']>
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Attorney information</h2>
        <p className="text-slate-300">
          Add your attorney's name and email so you can share documents later.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label htmlFor="attorney-name" className="block text-sm font-medium text-slate-300 mb-2">
            Attorney name <span className="text-red-400">*</span>
          </label>
          <input
            id="attorney-name"
            type="text"
            value={formData.attorney?.name || ''}
            onChange={(e) => updateAttorney('name', e.target.value)}
            placeholder="e.g., Jane Smith"
            className="w-full px-4 py-3 rounded-lg border border-slate-700/50 bg-slate-800/50 text-slate-50 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent"
          />
        </div>

        <div>
          <label htmlFor="attorney-email" className="block text-sm font-medium text-slate-300 mb-2">
            Attorney email <span className="text-red-400">*</span>
          </label>
          <input
            id="attorney-email"
            type="email"
            value={formData.attorney?.email || ''}
            onChange={(e) => updateAttorney('email', e.target.value)}
            placeholder="e.g., jane.smith@lawfirm.com"
            className="w-full px-4 py-3 rounded-lg border border-slate-700/50 bg-slate-800/50 text-slate-50 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent"
          />
        </div>

        <div>
          <label htmlFor="attorney-firm" className="block text-sm font-medium text-slate-300 mb-2">
            Law firm <span className="text-slate-500">(optional)</span>
          </label>
          <input
            id="attorney-firm"
            type="text"
            value={formData.attorney?.firm || ''}
            onChange={(e) => updateAttorney('firm', e.target.value)}
            placeholder="e.g., Smith & Associates"
            className="w-full px-4 py-3 rounded-lg border border-slate-700/50 bg-slate-800/50 text-slate-50 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent"
          />
        </div>

        <div>
          <label htmlFor="attorney-phone" className="block text-sm font-medium text-slate-300 mb-2">
            Phone <span className="text-slate-500">(optional)</span>
          </label>
          <input
            id="attorney-phone"
            type="tel"
            value={formData.attorney?.phone || ''}
            onChange={(e) => updateAttorney('phone', e.target.value)}
            placeholder="e.g., (555) 123-4567"
            className="w-full px-4 py-3 rounded-lg border border-slate-700/50 bg-slate-800/50 text-slate-50 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent"
          />
        </div>
      </div>
    </div>
  );
}

function CaseStep({ formData, setFormData }: { formData: SetupDraft; setFormData: (data: SetupDraft) => void }) {
  const updateCase = (field: keyof NonNullable<SetupDraft['case']>, value: string) => {
    setFormData({
      ...formData,
      case: {
        ...formData.case,
        [field]: value
      } as NonNullable<SetupDraft['case']>
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">About your case</h2>
        <p className="text-slate-300">
          If you already filed something with the court, add what you know. It's okay to leave blanks.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-300 mb-3">
          Have you already filed with the court?
        </label>
        <div className="flex gap-4">
          <button
            onClick={() => setFormData({ ...formData, hasFiledCase: true })}
            className={`flex-1 px-6 py-4 rounded-lg border-2 transition-all ${
              formData.hasFiledCase === true
                ? 'border-brand-orange bg-brand-orange/10 text-white'
                : 'border-slate-700/50 bg-slate-800/50 text-slate-300 hover:border-slate-600'
            }`}
          >
            Yes
          </button>
          <button
            onClick={() => setFormData({ ...formData, hasFiledCase: false, case: undefined })}
            className={`flex-1 px-6 py-4 rounded-lg border-2 transition-all ${
              formData.hasFiledCase === false
                ? 'border-brand-orange bg-brand-orange/10 text-white'
                : 'border-slate-700/50 bg-slate-800/50 text-slate-300 hover:border-slate-600'
            }`}
          >
            No, not yet
          </button>
        </div>
      </div>

      {formData.hasFiledCase && (
        <div className="space-y-4 mt-6">
          <div>
            <label htmlFor="case-title" className="block text-sm font-medium text-slate-300 mb-2">
              What is your case about? <span className="text-red-400">*</span>
            </label>
            <input
              id="case-title"
              type="text"
              value={formData.case?.title || ''}
              onChange={(e) => updateCase('title', e.target.value)}
              placeholder="e.g., Child Custody, Divorce, Restraining Order"
              className="w-full px-4 py-3 rounded-lg border border-slate-700/50 bg-slate-800/50 text-slate-50 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="case-number" className="block text-sm font-medium text-slate-300 mb-2">
              Case number <span className="text-slate-500">(optional)</span>
            </label>
            <input
              id="case-number"
              type="text"
              value={formData.case?.caseNumber || ''}
              onChange={(e) => updateCase('caseNumber', e.target.value)}
              placeholder="e.g., FL-2024-12345"
              className="w-full px-4 py-3 rounded-lg border border-slate-700/50 bg-slate-800/50 text-slate-50 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="case-court" className="block text-sm font-medium text-slate-300 mb-2">
              Court name <span className="text-slate-500">(optional)</span>
            </label>
            <input
              id="case-court"
              type="text"
              value={formData.case?.court || ''}
              onChange={(e) => updateCase('court', e.target.value)}
              placeholder="e.g., Superior Court"
              className="w-full px-4 py-3 rounded-lg border border-slate-700/50 bg-slate-800/50 text-slate-50 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="case-county" className="block text-sm font-medium text-slate-300 mb-2">
              County <span className="text-slate-500">(optional)</span>
            </label>
            <input
              id="case-county"
              type="text"
              value={formData.case?.county || ''}
              onChange={(e) => updateCase('county', e.target.value)}
              placeholder="e.g., Multnomah County"
              className="w-full px-4 py-3 rounded-lg border border-slate-700/50 bg-slate-800/50 text-slate-50 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent"
            />
          </div>
        </div>
      )}

      {formData.hasFiledCase === false && (
        <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg text-sm text-blue-200">
          No problem! You can add case details later when you're ready to file.
        </div>
      )}
    </div>
  );
}

function JournalStep({ formData, setFormData }: { formData: SetupDraft; setFormData: (data: SetupDraft) => void }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Your first journal entry</h2>
        <p className="text-slate-300">
          Start documenting your case. You can write about what's happening, your concerns, or what brought you here.
        </p>
      </div>

      <div>
        <label htmlFor="journal-entry" className="block text-sm font-medium text-slate-300 mb-2">
          What's on your mind today?
        </label>
        <textarea
          id="journal-entry"
          value={formData.initialJournalEntry}
          onChange={(e) => setFormData({ ...formData, initialJournalEntry: e.target.value })}
          rows={8}
          className="w-full px-4 py-3 rounded-lg border border-slate-700/50 bg-slate-800/50 text-slate-50 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent resize-none"
          placeholder="Write your first entry..."
        />
        <p className="mt-2 text-sm text-slate-400">
          {formData.initialJournalEntry.trim().length} characters
        </p>
      </div>

      <div className="p-4 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-300">
        <p className="font-semibold mb-2">💡 Tip</p>
        <p>
          Your journal is private and helps you track important events, conversations, and thoughts about your case. 
          Write as much or as little as you want.
        </p>
      </div>
    </div>
  );
}

function ReviewStep({ formData }: { formData: SetupDraft }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Almost done!</h2>
        <p className="text-slate-300">
          Review your information below. You can change any of this later.
        </p>
      </div>

      <div className="space-y-4">
        <div className="p-4 bg-slate-800/50 border border-slate-700/50 rounded-lg">
          <div className="text-sm text-slate-400 mb-1">State</div>
          <div className="text-white font-medium">{formData.filedState}</div>
        </div>

        {formData.hasAttorney && formData.attorney && (
          <div className="p-4 bg-slate-800/50 border border-slate-700/50 rounded-lg">
            <div className="text-sm text-slate-400 mb-2">Attorney</div>
            <div className="space-y-1">
              <div className="text-white font-medium">{formData.attorney.name}</div>
              <div className="text-slate-300 text-sm">{formData.attorney.email}</div>
              {formData.attorney.firm && (
                <div className="text-slate-400 text-sm">{formData.attorney.firm}</div>
              )}
            </div>
          </div>
        )}

        {formData.hasFiledCase && formData.case && (
          <div className="p-4 bg-slate-800/50 border border-slate-700/50 rounded-lg">
            <div className="text-sm text-slate-400 mb-2">Case</div>
            <div className="space-y-1">
              <div className="text-white font-medium">{formData.case.title}</div>
              {formData.case.caseNumber && (
                <div className="text-slate-300 text-sm">Case #{formData.case.caseNumber}</div>
              )}
              {formData.case.court && (
                <div className="text-slate-400 text-sm">{formData.case.court}</div>
              )}
            </div>
          </div>
        )}

        <div className="p-4 bg-slate-800/50 border border-slate-700/50 rounded-lg">
          <div className="text-sm text-slate-400 mb-2">First Journal Entry</div>
          <div className="text-slate-300 text-sm line-clamp-3">{formData.initialJournalEntry}</div>
        </div>
      </div>
    </div>
  );
}
